import React, { useState, useCallback } from 'react';
import PixelGradeBadge from './PixelGradeBadge';
import { scoreToGrade } from '../utils/GradeUtils';
import OrgMosaicGrid from './OrgMosaicGrid';

interface ModuleChange {
  name: string;
  before: 'pass' | 'warn' | 'fail';
  after: 'pass' | 'warn' | 'fail';
  endpointCount?: number;
}

interface RemediationResult {
  beforeScore: number;
  afterScore: number;
  changes: ModuleChange[];
  modulesPassingBefore: number;
  modulesPassingAfter: number;
}

interface VerifyRemediationProps {
  clientName: string;
  currentScore: number;
  onVerify?: () => Promise<RemediationResult>;
}

// No simulated data — verification requires a real agent scan

const VerifyRemediation: React.FC<VerifyRemediationProps> = ({ clientName, currentScore, onVerify }) => {
  const [state, setState] = useState<'idle' | 'scanning' | 'complete'>('idle');
  const [result, setResult] = useState<RemediationResult | null>(null);

  const handleVerify = useCallback(async () => {
    if (onVerify) {
      setState('scanning');
      try {
        const res = await onVerify();
        setResult(res);
        setState('complete');
      } catch {
        setState('idle');
      }
    } else {
      // No verification callback provided — show guidance
      setState('complete');
      setResult({
        beforeScore: currentScore,
        afterScore: currentScore,
        changes: [],
        modulesPassingBefore: 0,
        modulesPassingAfter: 0,
      });
    }
  }, [currentScore, onVerify]);

  const handleDismiss = () => {
    setState('idle');
    setResult(null);
  };

  // Scanning state
  if (state === 'scanning') {
    return (
      <div className="bg-white/[0.02] border border-anchor-blue/20 rounded-xl p-6 text-center">
        <div className="mb-4">
          <OrgMosaicGrid size={80} />
        </div>
        <div className="animate-pulse">
          <p className="text-sm text-anchor-blue font-medium">Raising Anchor...</p>
          <p className="text-xs text-white/40 mt-1">Running verification assessment for {clientName}</p>
        </div>
        <div className="mt-4 h-1 bg-white/[0.04] rounded-full overflow-hidden">
          <div className="h-full bg-anchor-blue rounded-full animate-[scan_3s_ease-in-out_infinite]" style={{ width: '60%', animation: 'scan 3s ease-in-out infinite' }} />
        </div>
        <style>{`@keyframes scan { 0%{width:0%;margin-left:0} 50%{width:60%;margin-left:20%} 100%{width:0%;margin-left:100%} }`}</style>
      </div>
    );
  }

  // Complete state — before/after comparison
  if (state === 'complete' && result) {
    const beforeGrade = scoreToGrade(result.beforeScore);
    const afterGrade = scoreToGrade(result.afterScore);
    const gradeChanged = beforeGrade.grade !== afterGrade.grade;
    const improved = result.afterScore > result.beforeScore;
    const delta = result.afterScore - result.beforeScore;

    return (
      <div className={`bg-white/[0.02] border rounded-xl p-6 ${gradeChanged ? 'border-anchor-teal/30' : 'border-white/[0.06]'}`}>
        <div className="flex items-center gap-2 mb-4">
          <div className={`w-2 h-2 rounded-full ${improved ? 'bg-anchor-teal' : 'bg-amber-400'}`} />
          <h3 className="text-sm font-medium text-white">
            {improved ? 'Remediation Verified' : 'Anchor Drifting'}
          </h3>
        </div>

        {/* Before / After comparison */}
        <div className="flex items-center justify-around py-4 mb-4 bg-white/[0.01] border border-white/[0.03] rounded-lg">
          <div className="text-center">
            <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Before</p>
            <PixelGradeBadge score={result.beforeScore} size={40} />
            <p className="text-lg font-light mt-1" style={{ color: beforeGrade.color }}>{beforeGrade.grade}</p>
            <p className="text-xs text-white/40">{result.beforeScore}%</p>
          </div>

          <div className="text-2xl text-white/20">→</div>

          <div className="text-center">
            <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">After</p>
            <PixelGradeBadge score={result.afterScore} size={40} />
            <p className="text-lg font-light mt-1" style={{ color: afterGrade.color }}>{afterGrade.grade}</p>
            <p className="text-xs text-white/40">{result.afterScore}%</p>
          </div>
        </div>

        {/* Changes detected */}
        <div className="space-y-1.5 mb-4">
          <p className="text-xs text-white/50 mb-2">Changes Detected:</p>
          {result.changes.map((change, i) => {
            const improved = change.before !== 'pass' && change.after === 'pass';
            const unchanged = change.before === change.after;
            return (
              <div key={i} className="flex items-center gap-2 text-xs py-1">
                <span className={`w-4 text-center ${improved ? 'text-anchor-teal' : unchanged ? 'text-white/20' : 'text-amber-400'}`}>
                  {improved ? '✓' : unchanged ? '─' : '!'}
                </span>
                <span className="text-white/70 flex-1">{change.name}</span>
                <span className={`${improved ? 'text-anchor-teal' : unchanged ? 'text-white/30' : 'text-amber-400'}`}>
                  {change.before.toUpperCase()} → {change.after.toUpperCase()}
                  {change.endpointCount ? ` (${change.endpointCount} ep)` : ''}
                </span>
              </div>
            );
          })}
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-2 gap-2 py-3 border-t border-white/[0.04] mb-4">
          <div className="text-xs">
            <span className="text-white/40">Score: </span>
            <span className="text-white/70">{result.beforeScore}% → {result.afterScore}%</span>
            <span className={`ml-1 ${delta > 0 ? 'text-anchor-teal' : 'text-amber-400'}`}>({delta > 0 ? '+' : ''}{delta}%)</span>
          </div>
          <div className="text-xs">
            <span className="text-white/40">Modules: </span>
            <span className="text-white/70">{result.modulesPassingBefore} → {result.modulesPassingAfter}</span>
            {result.modulesPassingAfter > result.modulesPassingBefore && (
              <span className="text-anchor-teal ml-1">(+{result.modulesPassingAfter - result.modulesPassingBefore})</span>
            )}
          </div>
          {gradeChanged && (
            <div className="col-span-2 text-xs">
              <span className="text-white/40">Grade: </span>
              <span style={{ color: beforeGrade.color }}>{beforeGrade.grade}</span>
              <span className="text-white/40"> → </span>
              <span style={{ color: afterGrade.color }}>{afterGrade.grade}</span>
              <span className="text-anchor-teal ml-1">(upgraded!)</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button onClick={handleDismiss} className="flex-1 py-2 px-4 bg-white/[0.03] border border-white/[0.06] rounded-lg text-xs text-white/50 hover:text-white/70 transition-colors">
            Dismiss
          </button>
          <button onClick={handleVerify} className="flex-1 py-2 px-4 bg-anchor-blue/10 border border-anchor-blue/30 rounded-lg text-xs text-anchor-blue hover:bg-anchor-blue/20 transition-colors">
            Verify Again
          </button>
        </div>
      </div>
    );
  }

  // Idle state — just the button
  return (
    <button
      onClick={handleVerify}
      className="w-full py-3 px-4 bg-anchor-blue/10 border border-anchor-blue/30 rounded-xl text-sm text-anchor-blue hover:bg-anchor-blue/20 transition-colors flex items-center justify-center gap-2"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
        <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
      </svg>
      Verify Remediation — {clientName}
    </button>
  );
};

export default VerifyRemediation;
